export type PreferredContactMethod = 'email' | 'phone' | 'text';

export interface Address {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface PassportInfo {
  number: string;
  countryOfIssue: string;
  dateOfIssue: string | null; // YYYY-MM-DD
  dateOfExpiry: string | null; // YYYY-MM-DD
  placeOfBirth: string;
}

export interface DriverLicenseInfo {
  number: string;
  state: string;
  expiration: string | null; // YYYY-MM-DD
}

export type RelationshipType =
  | 'spouse'
  | 'child'
  | 'parent'
  | 'sibling'
  | 'friend'
  | 'coworker'
  | 'other';

export type AirClass = 'any' | 'business' | 'first';

export type FoodPreference =
  | 'vegetarian'
  | 'vegan'
  | 'gluten-free'
  | 'dairy-free'
  | 'kosher'
  | 'halal'
  | 'low-carb'
  | 'pescatarian'
  | 'none';

export type ActivityPreference =
  | 'museums'
  | 'outdoor'
  | 'cultural'
  | 'food'
  | 'shopping'
  | 'nightlife'
  | 'adventure'
  | 'relaxation'
  | 'history'
  | 'art';

export type AccommodationType =
  | 'boutique'
  | 'luxury'
  | 'budget'
  | 'chain'
  | 'resort'
  | 'home-rental';

export type TravelStyle = 'relaxed' | 'moderate' | 'fast-paced';

export type TripType =
  | 'organized_tour'
  | 'cruise'
  | 'business'
  | 'backpacking'
  | 'luxury'
  | 'adventure'
  | 'cultural'
  | 'other';

export interface TravelCompanion {
  id: string;
  firstName: string;
  lastName: string;
  relationship: RelationshipType;
  age: number | null;
  passport: PassportInfo | null;
  driversLicense: DriverLicenseInfo | null;
  loyaltyPrograms: string[];
}

export interface TripPreferences {
  airClass: AirClass;
  food: FoodPreference[];
  activityPreferences: ActivityPreference[];
  accommodationType: AccommodationType[];
  accommodationBudgetPerNight: number | null;
  travelStyle: TravelStyle;
}

export type ItineraryItemType =
  | 'activity'
  | 'meal'
  | 'accommodation'
  | 'transport'
  | 'note'
  | 'other';

/** Place location on an itinerary item — mirrors iOS Location (address + optional coords). */
export interface ItineraryItemLocation {
  /** Stable Device Sync location id when known */
  id?: string;
  address: string;
  latitude?: number;
  longitude?: number;
}

/** QR / barcode on an itinerary item — mirrors iOS ActivityBarcodeExportData */
export interface ItineraryItemBarcode {
  type: string;
  payload: string;
  imagePath?: string | null;
  imageUrl?: string | null;
  imageHint?: string | null;
}

export interface ItineraryItemReminder {
  id: string;
  minutesBefore: number;
}

export interface ItineraryItemAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  createdAt: string;
}

export interface ItineraryItem {
  id: string;
  title: string;
  type: ItineraryItemType;
  time?: string; // HH:mm or h:mm A
  /** Duration in minutes */
  durationMinutes?: number;
  notes?: string;
  /** Website from place search (or manual) */
  url?: string;
  /** Phone from place search (or manual) */
  phone?: string;
  email?: string;
  provider?: string;
  reservationUrl?: string;
  reservationRequirement?: string;
  price?: number | null;
  currency?: string | null;
  priorityScore?: number;
  metadata?: Record<string, string>;
  reminders?: ItineraryItemReminder[];
  attachments?: ItineraryItemAttachment[];
  barcode?: ItineraryItemBarcode | null;
  /**
   * @deprecated Prefer `location.address`. Kept for reading older saved items.
   */
  address?: string;
  /** Address + optional lat/lng (filled when Places/geocode supplies coords). */
  location?: ItineraryItemLocation;
  /** Reference to List Builder activity when type is 'activity' */
  activityId?: string;
  /** Reference to List Builder meal when type is 'meal' */
  mealId?: string;
  /** Reference to List Builder hotel when type is 'accommodation' */
  hotelId?: string;
  /** Check-in date (YYYY-MM-DD) when type is 'accommodation' */
  accommodationCheckInDate?: string;
  /** Check-out date (YYYY-MM-DD) when type is 'accommodation' */
  accommodationCheckOutDate?: string;
  /** Confirmation or booking reference number */
  confirmationNumber?: string;
}

export interface ItineraryDay {
  date: string; // YYYY-MM-DD
  items: ItineraryItem[];
  /** List Builder city ID for this day – used to filter list builder options */
  cityId?: string;
  /** Device Sync day id when known */
  id?: string;
  notes?: string | null;
  timeZone?: string | null;
  /** Schema 1.3 — last content mutation on this day (ISO-8601 UTC); omit when unknown */
  updatedAt?: string | null;
}

/** City history snapshot stored on trip when a city is added to the trip */
export interface TripCityHistory {
  summary: string;
}

/** Per-city options / ideas checklist (consumer trips; optional on TP trips) */
export interface TripCityOption {
  id: string;
  title: string;
  description?: string;
  url?: string;
  completed: boolean;
  /** iOS OptionItem.address — preserved on Device Sync */
  address?: string;
  order?: number;
}

/** Hotel stay on a city visit — mirrors iOS HotelStayExportData */
export interface TripHotelStay {
  id: string;
  hotelName: string;
  checkInDate?: string | null;
  checkOutDate?: string | null;
  address?: string | null;
  phone?: string | null;
  notes?: string | null;
  confirmations?: string[];
}

/** AI plan selection on trip-v1 — mirrors iOS UserPlanSelectionExportData */
export interface TripUserPlanSelection {
  id: string;
  tripId: string;
  cityVisitId: string;
  itemTitle: string;
  normalizedTitle?: string;
  selectedSlot?: string;
  selectedDate?: string;
  itemData?: unknown;
  createdAt?: string;
}

export interface TripAttachmentRef {
  attachmentId: string;
  fileId: string;
}

/** Trip-level checklist item — mirrors iOS TripTaskItem / Device Sync taskItems */
export interface TripTaskItem {
  id: string;
  title: string;
  isDone: boolean;
  sortOrder: number;
}

export type CruisePortStatus = 'docked' | 'tendered' | 'cruising' | 'unknown';

/** Per-city visit metadata (dates / port times) — mirrors iOS CityVisit fields used on TripDetail */
export interface TripCityVisitMeta {
  arrivalDate: string | null; // YYYY-MM-DD
  departureDate: string | null; // YYYY-MM-DD
  /** Port arrival clock HH:mm (local), when known */
  portArrivalHm: string | null;
  /** Port departure clock HH:mm (local), when known */
  portDepartureHm: string | null;
  cruisePortStatus: CruisePortStatus | null;
  /** Dock / berth number when embark or debark */
  dockNumber?: string | null;
  /** Cruise embarkation port (local-only; not in Device Sync export yet) */
  isEmbarkation?: boolean;
  /** Cruise debarkation port (local-only; not in Device Sync export yet) */
  isDebarkation?: boolean;
  /** Country ISO2 from Device Sync city visit */
  countryISO2?: string | null;
  /** Country display name from Device Sync */
  countryName?: string | null;
  /** Destination currency code from Device Sync */
  currencyCode?: string | null;
  /** IANA timezone for the visit when known */
  timeZoneIdentifier?: string | null;
  /** Plan job id from POST /plan — mirrors iOS CityVisit.aiJobId */
  aiJobId?: string | null;
  /** ISO timestamp when the plan job was started */
  aiJobStartedAt?: string | null;
  /** queued | running | done | failed */
  aiStatus?: string | null;
  /** ISO timestamp of last AI status update */
  aiUpdatedAt?: string | null;
}

export interface Trip {
  id: string;
  title: string;
  tripType: TripType | null;
  info: string;
  startDate: string | null; // YYYY-MM-DD
  endDate: string | null;
  /** City IDs from List Builder – used to filter items when building itinerary */
  cityIds?: string[];
  /** Consumer-friendly city names when not using List Builder (keyed by city id) */
  cityNames?: Record<string, string>;
  /** Optional city coordinates from device sync (keyed by city visit id) */
  cityCoords?: Record<string, { latitude: number; longitude: number }>;
  /** Optional visit dates / port times (keyed by city visit id) */
  cityVisitMeta?: Record<string, TripCityVisitMeta>;
  /** History summaries for cities on this trip (keyed by city ID); set when city is added to trip */
  cityHistories?: Record<string, TripCityHistory>;
  /** Per-city options checklist (keyed by city ID) */
  cityOptions?: Record<string, TripCityOption[]>;
  /** Hotel stays keyed by city visit id — mirrors iOS CityVisit.hotelStays */
  hotelStaysByCity?: Record<string, TripHotelStay[]>;
  /** Trip-level checklist (Device Sync `taskItems`) — mirrors iOS TripTaskItem */
  taskItems?: TripTaskItem[];
  /** trip-v1 shell fields (Device Sync) */
  homeTimeZone?: string | null;
  currency?: string | null;
  temperatureUnit?: string | null;
  distanceUnit?: string | null;
  budget?: number | null;
  budgetCurrency?: string | null;
  ownerId?: string | null;
  canEdit?: boolean | null;
  /** Shared trip link `{authorSub}#trip#{tripId}` when materialized from a membership. */
  canonicalTripId?: string | null;
  /** Server overlay role: owner | editor | viewer */
  accessRole?: string | null;
  userPlanSelections?: TripUserPlanSelection[];
  attachmentFileIds?: string[];
  attachmentRefs?: TripAttachmentRef[];
  previewMapImageFileId?: string | null;
  /**
   * Cruise catalog itinerary id when this trip was imported on web.
   * Local fingerprint aid; iOS-originated trips may omit it.
   */
  sourceItineraryId?: string | null;
  /**
   * Schema 1.3 — last user-visible content mutation (ISO-8601 UTC).
   * Distinct from Device Sync catalog/envelope `updatedAt`. Omit when unknown.
   */
  contentUpdatedAt?: string | null;
  groupSize: number | null;
  adults: number | null;
  children: number | null;
  childrenAges: number[];
  /**
   * CRM / List Builder trip preferences (pro clients key-value API).
   * Not the same as iOS `/users/me/preferences` (see userPreferencesSyncService).
   */
  preferences: TripPreferences;
  travelCompanions: TravelCompanion[];
  itineraryDays?: ItineraryDay[];
}

export interface ClientNote {
  id: string;
  text: string;
  createdAt: string; // ISO date string
}

export interface LoyaltyProgramEntry {
  id: string;
  program: string;
  number: string;
  preferred: boolean;
}

export interface Client {
  id: string;
  firstName: string;
  lastName: string;
  mobilePhone: string;
  workPhone: string;
  homePhone: string;
  personalEmail: string;
  workEmail: string;
  address: Address;
  workAddress: Address;
  preferredContactMethod: PreferredContactMethod;
  passport: PassportInfo | null;
  driversLicense: DriverLicenseInfo | null;
  loyaltyPrograms: LoyaltyProgramEntry[];
  trips: Trip[];
  notes: ClientNote[];
}

// Legacy - for migration from old schema
export interface ClientContact {
  mobilePhone: string;
  workPhone: string;
  homePhone: string;
  workEmail: string;
  homeEmail: string;
  workAddress: string;
  homeAddress: string;
  preferredContactMethod: PreferredContactMethod;
}

export const createEmptyAddress = (): Address => ({
  line1: '',
  line2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
});

export const createEmptyPassport = (): PassportInfo => ({
  number: '',
  countryOfIssue: '',
  dateOfIssue: null,
  dateOfExpiry: null,
  placeOfBirth: '',
});

export const createEmptyDriverLicense = (): DriverLicenseInfo => ({
  number: '',
  state: '',
  expiration: null,
});

export const createEmptyTripPreferences = (): TripPreferences => ({
  airClass: 'any',
  food: [],
  activityPreferences: [],
  accommodationType: [],
  accommodationBudgetPerNight: null,
  travelStyle: 'moderate',
});

export const createEmptyTravelCompanion = (): Omit<TravelCompanion, 'id'> => ({
  firstName: '',
  lastName: '',
  relationship: 'other',
  age: null,
  passport: null,
  driversLicense: null,
  loyaltyPrograms: [],
});

export const createEmptyTrip = (): Omit<Trip, 'id'> => ({
  title: '',
  tripType: null,
  info: '',
  startDate: null,
  endDate: null,
  cityIds: [],
  cityNames: {},
  cityCoords: {},
  cityVisitMeta: {},
  cityHistories: {},
  cityOptions: {},
  taskItems: [],
  groupSize: null,
  adults: null,
  children: null,
  childrenAges: [],
  preferences: createEmptyTripPreferences(),
  travelCompanions: [],
  itineraryDays: [],
});

export const createEmptyContact = (): ClientContact => ({
  mobilePhone: '',
  workPhone: '',
  homePhone: '',
  workEmail: '',
  homeEmail: '',
  workAddress: '',
  homeAddress: '',
  preferredContactMethod: 'email',
});

export const createEmptyClient = (): Omit<Client, 'id'> => ({
  firstName: '',
  lastName: '',
  mobilePhone: '',
  workPhone: '',
  homePhone: '',
  personalEmail: '',
  workEmail: '',
  address: createEmptyAddress(),
  workAddress: createEmptyAddress(),
  preferredContactMethod: 'email',
  passport: null,
  driversLicense: null,
  loyaltyPrograms: [],
  trips: [{ ...createEmptyTrip(), id: crypto.randomUUID() }],
  notes: [],
});
